// ─── WebSocket Event Names ──────────────────────
export const WS_EVENTS = {
  SUBMIT_TURN: 'SUBMIT_TURN',
  STATE_UPDATE: 'STATE_UPDATE',
  MUTATION_EVENT: 'MUTATION_EVENT',
  SESSION_END: 'SESSION_END',
  SESSION_PAUSED: 'SESSION_PAUSED',
  TURN_REJECTED: 'TURN_REJECTED',
  TIME_WARNING: 'TIME_WARNING',
  RECONNECT: 'RECONNECT',
  STATE_SNAPSHOT: 'STATE_SNAPSHOT',
  AUTH_ERROR: 'AUTH_ERROR',
  HEARTBEAT: 'HEARTBEAT',
  HEARTBEAT_ACK: 'HEARTBEAT_ACK',
} as const;

// ─── Band Type ─────────────────────────────────
export type Band = 'low' | 'medium' | 'high';

// ─── Artifact Types ─────────────────────────────
export type ArtifactType = 'json_table' | 'text_doc' | 'code' | 'form' | 'timeline';

export type ScenarioArtifact = {
  artifactId: string;
  artifactType: ArtifactType;
  label: string;
  content: unknown;
  readable: boolean;
  editable: boolean;
};

export type ArtifactSnapshot = {
  artifactId: string;
  content: unknown;
};

// ─── Client → Server Payloads ──────────────────
export type SubmitTurnPayload = {
  simSessionId: string;
  text: string;
  reflection: string;
  artifactsBefore: ArtifactSnapshot[];
  artifactsAfter: ArtifactSnapshot[];
  artifactReadCount: number;
  artifactWriteCount: number;
};

export type ReconnectPayload = {
  simSessionId: string;
};

// ─── Server → Client Payloads ──────────────────
export type TimelineEntry = {
  turnIndex: number;
  phase: number;
  actionsDetected: string[];
  indicatorSnapshot: Record<string, Band>;
  reflectionProvided: boolean;
  timestamp: string;
};

export type StateUpdatePayload = {
  turnIndex: number;
  phase: number;
  indicators: Record<string, Band>;
  timelineEntry: TimelineEntry;
  actionsDetected: string[];
  turnAccepted: boolean;
};

export type MutationEventPayload = {
  description: string;
  injectedArtifacts?: ScenarioArtifact[];
  phase: 2;
};

export type SessionEndPayload = {
  outcome: 'success' | 'failure' | 'max_turns' | 'early_success';
  terminationReason: string;
  totalTurns: number;
  finalIndicators: Record<string, Band>;
};

export type TurnRejectedPayload = {
  reason: 'session_inactive' | 'auth_error' | 'invalid_payload' | 'processing_error';
  message: string;
};

export type TimeWarningPayload = {
  pctUsed: number;
  turnsRemaining: number;
};

export type StateSnapshotPayload = {
  turnIndex: number;
  phase: number;
  indicators: Record<string, Band>;
  timeline: TimelineEntry[];
  scenario: {
    narrativeContext: string;
    artifacts: ScenarioArtifact[];
  };
};

export type AuthErrorPayload = {
  reason: string;
  message: string;
};

export type SessionPausedPayload = {
  resumeDeadline: string | null;
};

// ─── Simulation State ──────────────────────────
export type SessionStatus =
  | 'loading'
  | 'pending'
  | 'scenarioReady'
  | 'active'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'timed_out'
  | 'disqualified';

export interface SimulationState {
  // Session Lifecycle
  sessionId: string | null;
  simSessionId: string | null;
  sessionStatus: SessionStatus;
  blueprintId: string | null;
  attemptNumber: number | null;

  // Scenario
  narrativeContext: string | null;
  artifacts: ScenarioArtifact[];

  // Turn State (banded only — NEVER raw values)
  turnIndex: number;
  phase: number;
  indicators: Record<string, Band>;
  actionsDetected: string[];

  // Timeline
  timeline: TimelineEntry[];

  // Input State
  actionText: string;
  reflectionText: string;
  isSubmitting: boolean;

  // Mutation
  mutationEvent: MutationEventPayload | null;
  showMutationOverlay: boolean;

  // Warnings & End
  timeWarning: TimeWarningPayload | null;
  sessionEnd: SessionEndPayload | null;
  turnRejected: TurnRejectedPayload | null;

  // Paused
  resumeDeadline: string | null;

  // Connection
  isConnected: boolean;
  isReconnecting: boolean;

  // Error
  error: string | null;
}

// ─── Actions ────────────────────────────────────
export type SimulationAction =
  | { type: 'SET_STATUS'; payload: SessionStatus }
  | { type: 'SESSION_CREATED'; payload: { sessionId: string; simSessionId: string; blueprintId?: string } }
  | { type: 'SCENARIO_LOADED'; payload: { narrativeContext: string; artifacts: ScenarioArtifact[] } }
  | { type: 'STATE_UPDATE'; payload: StateUpdatePayload }
  | { type: 'MUTATION_EVENT'; payload: MutationEventPayload }
  | { type: 'SESSION_END'; payload: SessionEndPayload }
  | { type: 'TURN_REJECTED'; payload: TurnRejectedPayload }
  | { type: 'TIME_WARNING'; payload: TimeWarningPayload }
  | { type: 'STATE_SNAPSHOT'; payload: StateSnapshotPayload }
  | { type: 'AUTH_ERROR'; payload: AuthErrorPayload }
  | { type: 'SESSION_PAUSED'; payload: SessionPausedPayload }
  | { type: 'SET_CONNECTED'; payload: boolean }
  | { type: 'SET_RECONNECTING'; payload: boolean }
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'SET_ACTION_TEXT'; payload: string }
  | { type: 'SET_REFLECTION_TEXT'; payload: string }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'DISMISS_MUTATION_OVERLAY' }
  | { type: 'UPDATE_ARTIFACT'; payload: { artifactId: string; content: unknown } }
  | { type: 'RESET' };
